import { Controller } from "@hotwired/stimulus"
import { areaStore } from "../../stores/locales/area_store"
import { useStore } from "stimulus-store"

import { sendFailureNotification } from "shared/toast_notifications";

// Connects to data-controller="floor-plan"
export default class extends Controller {

    static stores = [
        areaStore
    ]

    static targets = [
        "floorPlan",
        "title"
    ]

    static values = {
        defaultMessage: String
    }

    connect() {
        useStore(this)
        this.updateTitle("Select Area")
        this.showDefaultMessage()
    }

    /**
     * React to area store changes and display the floor plan
     */
    onAreaStoreUpdate(area) {
        if (area && area.id) {
            this.updateTitle(area.name)
            this.displayFloorPlan(area)
        } else {
            this.clearFloorPlan()
        }
    }

    /**
     * Display the floor plan for the selected area
     */
    displayFloorPlan(area) {
        if (!this.hasFloorPlanTarget) {
            console.warn('Floor plan target not found')
            return
        }

        // Preserve the icon container if it exists
        const iconContainer = this.floorPlanTarget.querySelector('#icon-container')

        // Clear existing content
        this.floorPlanTarget.innerHTML = ''

        // Restore the icon container if it existed
        if (iconContainer) {
            this.floorPlanTarget.appendChild(iconContainer)
        }

        if (area.floor_plan_url) {
            // Create the floor plan image element. The device-icon overlay
            // anchors to this <img> via getBoundingClientRect(), so the stored
            // floor plan is always a directly-displayable image set as this
            // element's src.
            const img = document.createElement('img')
            img.className = 'floor-plan-image img-fluid'
            img.alt = `Floor plan for ${ area.name }`

            // Add error handling for image loading
            img.onerror = () => {
                this.showNoFloorPlanMessage();
                sendFailureNotification(`Unable to load floor plan for ${ area.name }. Please try again.`);
            }

            // Insert the image before the icon container (so icons appear on top)
            if (iconContainer) {
                this.floorPlanTarget.insertBefore(img, iconContainer)
            } else {
                this.floorPlanTarget.appendChild(img)
            }

            // Stored floor plans are always directly-displayable images.
            img.src = area.floor_plan_url
        } else {
            // No floor plan available
            this.showNoFloorPlanMessage()
        }
    }

    /**
     * Update the title element with the given text
     */
    updateTitle(titleText) {
        if (!this.hasTitleTarget) {
            console.warn('Title target not found')
            return
        }

        // Replace the existing header with the title text
        this.titleTarget.textContent = titleText
    }

    /**
     * Show default message when no area is selected
     */
    showDefaultMessage() {
        if (!this.hasFloorPlanTarget) {
            return
        }

        // Preserve the icon container if it exists
        const iconContainer = this.floorPlanTarget.querySelector('#icon-container')

        const message = this.defaultMessageValue || 'Select an area to view its floor plan'
        this.floorPlanTarget.innerHTML = `<p class="text-muted text-center">${ this.escapeHtml(message) }</p>`

        // Restore the icon container if it existed
        if (iconContainer) {
            this.floorPlanTarget.appendChild(iconContainer)
        }
    }

    /**
     * Show gray placeholder with loading indicator when area has no floor plan
     */
    showNoFloorPlanMessage() {
        if (!this.hasFloorPlanTarget) {
            return
        }

        // Preserve the icon container if it exists
        const iconContainer = this.floorPlanTarget.querySelector('#icon-container')

        // Create gray placeholder container
        const placeholderContainer = document.createElement('div')
        placeholderContainer.className = 'text-center'

        // Create gray placeholder box
        const placeholderBox = document.createElement('div')
        placeholderBox.style.backgroundColor = '#e9ecef'
        placeholderBox.style.border = '2px dashed #dee2e6'
        placeholderBox.style.borderRadius = '8px'
        placeholderBox.style.width = '100%'
        placeholderBox.style.height = '400px'
        placeholderBox.style.display = 'flex'
        placeholderBox.style.alignItems = 'center'
        placeholderBox.style.justifyContent = 'center'
        placeholderBox.style.flexDirection = 'column'

        // Create placeholder text
        const placeholderText = document.createElement('p')
        placeholderText.className = 'text-muted mb-0'
        placeholderText.textContent = 'No floor plan available'

        // Assemble the placeholder
        placeholderBox.appendChild(placeholderText)
        placeholderContainer.appendChild(placeholderBox)

        this.floorPlanTarget.appendChild(placeholderContainer)

        // Restore the icon container if it existed (so icons can still appear)
        if (iconContainer) {
            this.floorPlanTarget.appendChild(iconContainer)
        }
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }

    /**
     * Clear the floor plan display
     */
    clearFloorPlan() {
        this.updateTitle("Select Area")
        this.showDefaultMessage()
    }
}